<template>
  <div class="calender-widget">  

    <!-- Notices -->
    <div v-if="occupancySum === 0" style="margin-top: -24px;">
      <div style="text-align: center;">
        <p style="background-color: #f7f7f7; padding: 20px;">
          {{ getTranslation('SELECT_PARTICIPANTS_TO_SEE_AVAILIBILITIES') }}
        </p>
      </div>
    </div>

    <!-- Select month -->
    <div class="month-selector">
      <div v-on:click="changeActiveDate(-1,0)" class="month-selector-item">
        <img class="arrow" alt="left-arrow" :src="require('../assets/arrow-left.png')" />
      </div>
      <div class="month-selector-item selected-month">
        {{ translateActiveMonth(activeMonth) }} {{ activeYear }}
      </div>
      <div v-on:click="changeActiveDate(1,0)" class="month-selector-item">
        <img class="arrow" alt="right-arrow" :src="require('../assets/arrow-right.png')" />
      </div>
    </div>
    <div v-if="calendarErrors" class="calender-errors">
      {{ getTranslation('COULDNT_LOAD_CALENDER') }}
      <br>
    </div>
    <div v-if="calendarLoading" class="calender-loading">
      <img
        style="height: 70px; width: 70px; padding: 10px; opacity: 0.4;"
        alt="loading-gif"
        :src="require('../assets/loader.gif')" />
    </div>

    <!-- Select day -->
    <div v-if="!calendarLoading && !calendarErrors" class="calender-container">

      <!-- Ma Ti Ke To Pe La Su - row -->
      <div class="week-row">
        <div
          class="day week-header"
          v-for="(day, index) in weekDays"
          :key="'d-' + index + '-' + day.date">
          <p>{{ day }}</p>
        </div>
      </div>

      <!-- Days -->
      <div class="week-row" v-for="(week, index) in weeks" :key="'w-' + index">
        <button
          v-for="(day, index) in week.days"
          :class="{
            'day': true,
            'invisible': day.notInCurrentMonth,
            'available': dayIsAvailable(day) && dayHasEnoughAvailability(day, occupancySum),
            'unavailable': dayIsAvailable(day) && !(dayHasEnoughAvailability(day, occupancySum)),
            'selected': day.fullDate === selectedDate
          }"          
          v-on:click="dayIsAvailable(day) && dayHasEnoughAvailability(day, occupancySum) && setSelectedDate(day)"
          :key="'d-' + index + '-m-' + activeMonth">
          <p>{{ day.date }}</p>
          <div class="price-area">
            <p
              v-if="dayIsAvailable(day)"
              class="price">
              {{ getSmallestPrice(day.totalPriceByRate) }}
            </p>
          </div>
        </button>
      </div>
    </div>

    <!-- Time & Rate -->
    <div v-if="selectedDate" class="time-select">

      <!-- Select time -->
      <h4 class="selected-month">{{ getTranslation('PICK_A_TIME') }}</h4>
      <div style="display: flex; flex-direction: row; align-items: center; justify-content: center; flex-wrap: wrap;">
        <div style="text-align: center; padding-left: 10px; padding-right: 10px;" v-for="availability in getAvailabilities(selectedDate)" :key="availability.fullDate">
          
          <button            
            :class="{
              'time-selection-btn': true,
              'unavailable': (occupancySum > availability.availabilityCount) || (availability.activityAvailability.minParticipantsToBookNow > occupancySum),
              'selected': availability.activityAvailability.startTime === selectedTime.time
            }"
            v-on:click="
              !(occupancySum > availability.availabilityCount) && !(availability.activityAvailability.minParticipantsToBookNow > occupancySum) &&
              setSelectedTime({
                time: availability.activityAvailability.startTime,
                id: availability.activityAvailability.startTimeId,
                occupancyMax: availability.availabilityCount,
                rates: availability.activityAvailability.ratesAvailable
              })" >
            {{ availability.activityAvailability.startTime }}
          </button>

          <div v-if="(availability.activityAvailability.minParticipantsToBookNow > 1) && (availability.activityAvailability.minParticipantsToBookNow > occupancySum)">
            <p style="margin-top: 3px; font-size: 0.6rem;">
              {{ getTranslation('REQUIRES_AT_LEAST_PARTICIPANTS_1') }} {{ availability.activityAvailability.minParticipantsToBookNow }} {{ getTranslation('REQUIRES_AT_LEAST_PARTICIPANTS_2') }}
            </p>
          </div>  

        </div>
      </div>

      <!-- Select rate -->
      <h4 v-if="!hideRateSelection && JSON.stringify(selectedTime) !== '{}'" class="selected-month">{{ getTranslation('CHOOSE_A_PRICE') }}</h4>
      <div
        v-if="!hideRateSelection && JSON.stringify(selectedTime) !== '{}' && selectedDate && getTargetDay(selectedDate) && getTargetDay(selectedDate).totalPriceByRate"
        style="display: flex; flex-direction: column;"
      >
        <div
          v-for="rate in getRatesBySelectedTime(getTargetDay(selectedDate).totalPriceByRate, selectedTime.rates)"
          :key="rate.rateId"
          class="rate-row"
        >        
          <div>
            <p style="flex: 1; text-align: left; margin-bottom: 3px; font-weight: 600;">
              {{ rateInfos.filter(_ => _.id === rate.rateId)[0].title }}
            </p>
            <p style="margin-top: 3px; text-align: left;">
              {{ getTranslation('REQUIRES_AT_LEAST_PARTICIPANTS_1') }} {{ rateInfos.filter(_ => _.id === rate.rateId)[0].minPerBooking }} {{ getTranslation('REQUIRES_AT_LEAST_PARTICIPANTS_2') }}
            </p>
          </div>     

          <div style="flex: 1; text-align: right;">
            <button
              :class="{
                'time-selection-btn': true,
                'selected': rate.rateId === selectedRate.rateId,
                'deactive': Object.values(participants).reduce((a,b) => a + (b.amount * b.occupancy), 0) < rateInfos.filter(_ => _.id === rate.rateId)[0].minPerBooking
              }"
              :disabled="Object.values(participants).reduce((a,b) => a + (b.amount * b.occupancy), 0) < rateInfos.filter(_ => _.id === rate.rateId)[0].minPerBooking"
              style="flex: 1;"
              v-on:click="setSelectedRate(rate, rateInfos)" >
              {{ rate.price }}            
            </button>        
          </div>        
        </div>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'Calender',
  props: {
    title: String,
    weeks: Array,
    calendarLoading: Boolean,
    selectedDate: String,
    occupancySum: Number,
    calendarErrors: String,
    selectedTime: Object,
    setSelectedTime: Function,
    selectedRate: Object,
    setSelectedRate: Function,
    rateInfos: Array,
    hideRateSelection: Boolean,
    getTranslation: Function,
    participants: Object,
    activeMonth: Number,
    setActiveMonth: Function,
    activeYear: Number,
    setActiveYear: Function       
  },
  data: function () {
    return {
      weekDays: ['M','T','K','T','P','L','S']
    }
  },
  methods: {
    setSelectedDate: function (day) {
      if (day.totalPriceByRate[0] && day.totalPriceByRate[0].price) {
        this.$emit('update-cart', [{}, day.fullDate, day.totalPriceByRate[0].price] )
      }
    },
    changeActiveDate: function (months, years) {
      let newMonth;
      let newYear;
      if ((this.activeMonth + months) > 12) {
        newMonth = this.activeMonth + months - 12
        newYear = this.activeYear + 1 + years
      } else if ((this.activeMonth + months) < 1) {
        newMonth = this.activeMonth + months + 12
        newYear = this.activeYear - 1 + years
      } else {
        newMonth = this.activeMonth + months
        newYear = this.activeYear + years
      }
      this.setActiveMonth(newMonth)
      this.setActiveYear(newYear)
      this.$emit('update-calender', { year: newYear, month: newMonth })
    },
    translateActiveMonth: function (monthInt) {
      if (monthInt === 1) return this.getTranslation('JANUARY')
      if (monthInt === 2) return this.getTranslation('FEBRUARY')
      if (monthInt === 3) return this.getTranslation('MARCH')
      if (monthInt === 4) return this.getTranslation('APRIL')
      if (monthInt === 5) return this.getTranslation('MAY')
      if (monthInt === 6) return this.getTranslation('JUNE')
      if (monthInt === 7) return this.getTranslation('JULY')
      if (monthInt === 8) return this.getTranslation('AUGUST')
      if (monthInt === 9) return this.getTranslation('SEPTEMBER')
      if (monthInt === 10) return this.getTranslation('OCTOBER')
      if (monthInt === 11) return this.getTranslation('NOVEMBER')
      if (monthInt === 12) return this.getTranslation('DECEMBER')
      return this.getTranslation('ERROR')
    },
    getAvailabilities: function (date) {
      if (!date) return ''
      const days = [].concat.apply([], this.weeks.map(_ => _.days));            
      const targetDay = days.filter(_ => _.fullDate === date)[0]
      if (!targetDay) {
        return []
      }
      return targetDay.availabilities
    },
    getTargetDay: function (date) {
      if (!date) return ''
      const days = [].concat.apply([], this.weeks.map(_ => _.days));            
      const targetDay = days.filter(_ => _.fullDate === date)[0]
      if (!targetDay) {
        return []
      }
      return targetDay
    },
    getRatesBySelectedTime: function (availableRates, targetRates) {
      return availableRates.filter(_ => {
        return targetRates && targetRates.includes(_.rateId)
      })
    },
    dayHasEnoughAvailability: function (day, selectedOccupancyAmount) {
      return day.availabilities &&
        (
          (day.availabilities[0] && day.availabilities[0].availabilityCount >= selectedOccupancyAmount) || 
          (day.availabilities[1] && day.availabilities[1].availabilityCount >= selectedOccupancyAmount) || 
          (day.availabilities[2] && day.availabilities[2].availabilityCount >= selectedOccupancyAmount) || 
          (day.availabilities[3] && day.availabilities[3].availabilityCount >= selectedOccupancyAmount) || 
          (day.availabilities[4] && day.availabilities[4].availabilityCount >= selectedOccupancyAmount) 
        )
    },
    dayIsAvailable: function (day) {
      return day.activityAvailableStatus === 'AVAILABLE' &&
        day.totalPriceByRate[0] &&
        day.totalPriceByRate[0].price &&
        day.totalPriceByRate[0].priceDetails.amount !== 0        
    },
    getSmallestPrice: function (prices) {
      // copy to prevent infinite loop in render
      const pricesCopy = JSON.parse(JSON.stringify(prices))
      const smallest = pricesCopy.sort((a, b) => a.priceDetails.amount - b.priceDetails.amount)[0]
      return smallest && smallest.price
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.deactive:hover {
  background-color: rgb(255,230,230) !important;
}
.rate-row {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
}
.time-selection-btn {
  background-color: #f6f6f6;
  border: 0px;
  padding: 15px;
  padding-left: 25px;
  padding-right: 25px;
  margin: 5px;
}
.time-selection-btn:hover {
  background-color: rgba(0,147,200,0.3);
}
.time-select {
  text-align: center;
  border-top: 1px solid rgba(0,0,0,0.03);
  padding: 10px;
  padding-top: 0px;
  padding-bottom: 0px;
}
.arrow {
  width: 25px;
  height: 20px;
  object-fit: contain;
}
.calender-widget {
  padding-top: 10px;
}
.calender-container {
  border: 0px solid rgba(0,0,0,0.03);
}
.calender-loading {
  text-align: center;
  padding-top: 70px;
  padding-bottom: 70px;
}
.calender-errors {
  padding: 10px;
  text-align: center;
}
.week-row {
  display: flex;
  flex-direction: row;
  margin-bottom: 0px;
}
.day {
  display: flex;
  flex-direction: column;
  flex: 1;
  border: 0px solid rgba(0,0,0,0.05);
  align-items: center;
  justify-content: center;
  background-color: rgba(255,255,255,1);
  font-size: 0.75rem;
  color: rgba(130,130,130,1);
  animation-name: fade-in;
  animation-duration: 1s;
}
.day:hover {
  
}
.month-selector {
  display: flex;
  flex-direction: row;
  padding-bottom: 10px;
  padding-top: 5px;
}
.month-selector-item {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
}
.selected-month {
  text-transform: uppercase;
  color: #0092C8;
  font-size: 0.8rem;
  font-weight: 600;
}
.week-header {
  border: 0px;
  font-size: 0.7rem;
  color: #0092C8;
  font-weight: 600;
}
.price-area {
  flex: 1;
  min-height: 14px;
}
.price {
  font-size: 0.55rem;
  margin-top: -7px;
}
.available {
  background-color: rgba(0,147,200,0.15);
}
.available:hover {
  background-color: rgba(0,147,200,0.3);
}
.unavailable {
  background-color: rgba(255,240,240);
  cursor: not-allowed;
}
.unavailable:hover {
  background-color: rgba(255,240,240);
}
.selected {
  background-color: rgba(0,147,200,1);
  color: white;
}
.selected:hover {
  background-color: rgba(0,147,200,1);
}
.invisible {
  color: rgba(0,0,0,0);
  background-color: rgba(0,0,0,0.03);
}
.invisible:hover {
  background-color: rgba(0,0,0,0.03);
}

@media only screen and (max-width: 450px) {
  .price {
    font-size: 0.47rem;
  }
}
</style>
